import getInjector from '../utils/getInjector';
import loggerService from '../utils/loggerService';

const DEFAULT_ITEM_TYPE = 'product';
const DEFAULT_OPEN_MINI_CART = true;
// Mirrors CartApi::Adapter::VALID_ITEM_TYPES (app/services/cart/adapters/cart_api.rb) —
// the backend silently skips items with any other type.
const VALID_ITEM_TYPES = [
  'product',
  'product_set',
  'subscription_product',
  'addon_product',
  'redeem_gift',
];

// updateItem/removeItem reject with the raw $http response (message under
// err.data.*); addItem's rejections are normalized by handleOverLimit()
// (services.cart.js) into { type: 'overLimit' } regardless of cause, so no
// backend message is available there — fall back to a generic message.
function extractErrorMessage(err) {
  return (
    err?.data?.error_message ??
    err?.data?.error ??
    (err?.type === 'overLimit' ? 'Exceeded cart limitation' : undefined) ??
    err?.message ??
    String(err)
  );
}

function fail(message) {
  throw { success: false, message };
}

async function _getService(name) {
  const injector = await getInjector();
  if (!injector) {
    throw new Error(`Unable to access ${name}: Angular not loaded`);
  }
  return injector.get(name);
}

const CartAppExtensionService = {
  async addCartItem(
    appId,
    {
      type = DEFAULT_ITEM_TYPE,
      product_id,
      variation_id,
      quantity,
      item_data,
      open_mini_cart = DEFAULT_OPEN_MINI_CART,
    },
  ) {
    if (!appId) {
      fail('appId is required');
    }
    if (!product_id) {
      fail('product_id is required');
    }
    if (typeof quantity !== 'number' || quantity < 1) {
      fail('quantity must be a number >= 1');
    }

    let cartService;
    let trackerService;
    try {
      cartService = await _getService('cartService');
      trackerService = await _getService('trackerService');
    } catch (err) {
      loggerService.error(err, { tags: { function: 'addCartItem' } });
      fail(extractErrorMessage(err));
    }

    return cartService
      .addItem(
        product_id,
        {
          type,
          quantity,
          variation: variation_id ? { key: variation_id } : undefined,
          productSetData: item_data?.selected_child_products,
        },
        { app_id: appId },
      )
      .then((responseCartData) => {
        // itemAfterAdded is cartService's own precise match (product_set_revamp);
        // fall back to product_id/variation_id for everything else.
        const item =
          responseCartData.itemAfterAdded ??
          responseCartData.items.find(
            (candidate) =>
              candidate.product_id === product_id &&
              (candidate.variation_id || '') === (variation_id || ''),
          );

        if (item) {
          const itemPrice = cartService.getItemPrice(item)?.dollars ?? 0;
          trackerService.track({
            type: trackerService.generalEventType.ADD_TO_CART,
            data: {
              items: [
                {
                  product: item.product,
                  value: quantity * itemPrice,
                  quantity,
                  variationSelected: item.variation,
                },
              ],
              eventId: responseCartData.event_id,
            },
          });

          if (open_mini_cart) {
            jQuery.sidr('open', 'cart-panel');
          }
        }

        return { success: true };
      })
      .catch((err) => fail(extractErrorMessage(err)));
  },

  async addCartItems(
    appId,
    { main_product_id, items, open_mini_cart = DEFAULT_OPEN_MINI_CART },
  ) {
    if (!appId) {
      fail('appId is required');
    }
    if (!main_product_id) {
      fail('main_product_id is required');
    }
    if (!Array.isArray(items) || items.length === 0) {
      fail('items must be a non-empty array');
    }
    for (const item of items) {
      if (!item || typeof item !== 'object') {
        fail('items[] entries must be objects');
      }
      if (!item.product_id) {
        fail('items[].product_id is required');
      }
      if (typeof item.quantity !== 'number' || item.quantity < 1) {
        fail('items[].quantity must be a number >= 1');
      }
      if (item.type && !VALID_ITEM_TYPES.includes(item.type)) {
        fail(`items[].type must be one of: ${VALID_ITEM_TYPES.join(', ')}`);
      }
    }

    let cartService;
    let trackerService;
    try {
      cartService = await _getService('cartService');
      trackerService = await _getService('trackerService');
    } catch (err) {
      loggerService.error(err, { tags: { function: 'addCartItems' } });
      fail(extractErrorMessage(err));
    }

    const mappedItems = items.map(
      ({
        type = DEFAULT_ITEM_TYPE,
        product_id,
        variation_id,
        quantity,
        item_data,
      }) => ({
        type,
        product_id,
        quantity,
        variation_id: variation_id ? { key: variation_id } : undefined,
        productSetData: item_data?.selected_child_products,
      }),
    );

    return cartService
      .addItems(
        main_product_id,
        { items: mappedItems, value: 0 },
        {
          app_id: appId,
        },
      )
      .then((responseCartData) => {
        const mainRequestedItem = items.find(
          (item) => item.product_id === main_product_id,
        );
        const mainItem = mainRequestedItem
          ? responseCartData.items.find(
              (candidate) =>
                candidate.product_id === main_product_id &&
                (candidate.variation_id || '') ===
                  (mainRequestedItem.variation_id || ''),
            )
          : undefined;

        if (mainItem) {
          const itemPrice = cartService.getItemPrice(mainItem)?.dollars ?? 0;
          trackerService.track({
            type: trackerService.generalEventType.ADD_TO_CART,
            data: {
              items: [
                {
                  product: mainItem.product,
                  value: mainRequestedItem.quantity * itemPrice,
                  quantity: mainRequestedItem.quantity,
                  variationSelected: mainItem.variation,
                },
              ],
              eventId: responseCartData.event_id,
            },
          });

          if (open_mini_cart) {
            jQuery.sidr('open', 'cart-panel');
          }
        }

        return { success: true };
      })
      .catch((err) => fail(extractErrorMessage(err)));
  },

  async updateCartItemQuantity(appId, { item_id, variation_id, quantity }) {
    if (!appId) {
      fail('appId is required');
    }
    if (!item_id) {
      fail('item_id is required');
    }
    if (typeof quantity !== 'number' || quantity < 0) {
      fail('quantity must be a number >= 0');
    }

    let cartService;
    try {
      cartService = await _getService('cartService');
    } catch (err) {
      loggerService.error(err, {
        tags: { function: 'updateCartItemQuantity' },
      });
      fail(extractErrorMessage(err));
    }

    // quantity can be 0 — updateItem/the downstream service treats that as removal
    return cartService
      .updateItem(item_id, variation_id, quantity, null, { app_id: appId })
      .then(() => ({ success: true }))
      .catch((err) => fail(extractErrorMessage(err)));
  },

  async removeCartItem(appId, { item_id }) {
    if (!appId) {
      fail('appId is required');
    }
    if (!item_id) {
      fail('item_id is required');
    }

    let cartService;
    try {
      cartService = await _getService('cartService');
    } catch (err) {
      loggerService.error(err, { tags: { function: 'removeCartItem' } });
      fail(extractErrorMessage(err));
    }

    return cartService
      .removeItem(item_id, null, { app_id: appId })
      .then(() => ({ success: true }))
      .catch((err) => fail(extractErrorMessage(err)));
  },
};

export default CartAppExtensionService;
